import { Dialog, Transition, TransitionChild } from '@headlessui/react'
import { MutableRefObject, ReactNode } from 'react'

import cn from '@/utilities/cn'

import PageContainer, { PageProvider } from './PageContainer'

export type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'

interface PageModalProps {
  initialFocus?: MutableRefObject<any>
  className?: string
  isOpen?: boolean
  onClose?: () => void
  onSelect?: (values: any) => void
  children: ReactNode
  values?: any
  wrapperComponent?: (currentPage: ReactNode, values: PageProvider) => ReactNode
  size?: Size
}

const PageModal = ({
  initialFocus,
  className,
  isOpen = true,
  onClose = () => {},
  onSelect = () => {},
  children,
  values,
  wrapperComponent,
  size = 'lg'
}: PageModalProps) => {
  return (
    <Transition show={isOpen}>
      <Dialog as="div" className="relative z-10" onClose={onClose} initialFocus={initialFocus}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center px-4 text-center sm:items-center sm:p-4">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={cn(
                  {
                    'w-full px-2 lg:w-1/3': size === 'xs',
                    'w-full px-2 lg:w-1/2': size === 'sm',
                    'w-full px-4 lg:w-2/3': size === 'md',
                    'w-full px-6 lg:w-3/4': size === 'lg',
                    'w-full px-6 lg:w-5/6': size === 'xl',
                    'w-full px-6': size === 'full'
                  },
                  'relative w-fit transform overflow-hidden rounded-lg bg-white py-4 text-left shadow-xl transition-all',
                  className
                )}
              >
                <PageContainer
                  wrapperComponent={wrapperComponent}
                  onClose={onClose}
                  onSelect={onSelect}
                  values={values}
                >
                  {currentPage => {
                    if (currentPage) return currentPage
                    else return children
                  }}
                </PageContainer>
              </Dialog.Panel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default PageModal
